import { useTranslation } from "../../core/translation.core";
import { EditorSection, Title } from "../reuseables/editor.comp";
import SingleUserSearchInput, { SelectedUser } from "../user/singleUserSearchInput.core";
import DisplayAnimate from "../reuseables/displayAnimate.comp";
import { UseFormReturn } from "react-hook-form";
import { useState } from "react";
import TrashIcon from "../../icons/trash.icon";
import { LabelAndInput } from "../reuseables/form.comp";
import { toast } from "react-toastify";
import { ManualOrderFormData } from "../../types/order";

export default function ManualOrderCustomer({ methods }: { methods: UseFormReturn<ManualOrderFormData, any> }) {
  const { t, msg } = useTranslation();
  const [mode, setMode] = useState<"display" | "addUser">("display");
  const customerValue = methods.watch("userId", "");

  function addUser(userId: string, userDetails: { firstName: string; lastName: string; email: string; mobileNumber?: string }) {
    methods.setValue("userId", userId);
    methods.setValue("customer", {
      id: userId,
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      email: userDetails.email,
      mobileNumber: userDetails.mobileNumber,
    });
  }

  return (
    <EditorSection>
      <Title>{t(msg.customer.customer)}</Title>
      <div className="flex flex-col">
        <div className="flex flex-row">
          <div className="flex-1 ">
            <SingleUserSearchInput addUser={addUser} />
          </div>
          {customerValue === "" ? (
            <button
              id="add-customer"
              onClick={() => setMode(mode === "addUser" ? "display" : "addUser")}
              type="button"
              className={`h-9 px-3 ml-2 text-white rounded-md ${mode === "addUser" ? "bg-red-600" : "bg-green-600"}`}
            >
              {mode === "display" ? t(msg.user.addNewCustomer) : t(msg.form.cancel)}
            </button>
          ) : (
            <></>
          )}
        </div>
        {mode === "addUser" && (
          <DisplayAnimate>
            <AddNewUser methods={methods} addUser={addUser} />
          </DisplayAnimate>
        )}
      </div>
    </EditorSection>
  );
}

function AddNewUser({
  methods,
  addUser,
}: {
  methods: UseFormReturn<any, any>;
  addUser: (userId: string, userDetails: { firstName: string; lastName: string; email: string }) => void;
}) {
  const { t, msg } = useTranslation();
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);

  function handleUser(user: SelectedUser) {
    setSelectedUser(user);
    addUser(user.id, {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    });
    methods.setValue("userId", user.id);
    methods.setValue("customer", {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      mobile: user.mobile,
    });
  }

  function clearSelection() {
    setSelectedUser(null);
    methods.setValue("userId", "");
    methods.setValue("customer", {
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      mobile: "",
    });
    addUser("", { firstName: "", lastName: "", email: "" });
  }

  return (
    <div className="flex flex-col gap-4 my-4">
      {selectedUser ? (
        <div className="flex items-center justify-between border rounded-lg p-4">
          <div>
            <p className="font-medium uppercase ">
              {selectedUser.firstName.toUpperCase()} {selectedUser.lastName.toUpperCase()}
            </p>
            <p className="text-sm text-gray-600 dark:text-white">{selectedUser.email}</p>
            <p className="text-sm text-gray-600 dark:text-white">{selectedUser.mobile}</p>
          </div>
          <button onClick={clearSelection} className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg">
            <TrashIcon className="w-6 h-6" />
          </button>
        </div>
      ) : (
        <>
          <p className="mr-2">{t(msg.user.addUser)}</p>
          <div className="flex flex-row gap-4">
            <LabelAndInput
              type="text"
              register={methods.register}
              fieldName="firstName"
              topLabelCss="w-full"
              labelCss="-mb-[.3px]"
              registerOptions={{ min: 0 }}
              label={t(msg.user.firstname)}
              errors={methods.formState.errors}
              required
            />
            <LabelAndInput
              type="text"
              register={methods.register}
              fieldName="lastName"
              topLabelCss="w-full"
              labelCss="-mb-[.3px]"
              registerOptions={{ min: 0 }}
              label={t(msg.user.lastname)}
              errors={methods.formState.errors}
              required
            />
          </div>
          <div className="flex flex-row gap-4">
            <LabelAndInput
              type="text"
              register={methods.register}
              fieldName="email"
              topLabelCss="w-full"
              labelCss="-mb-[.3px]"
              registerOptions={{ min: 0 }}
              label={t(msg.user.email)}
              errors={methods.formState.errors}
              required
            />
            <LabelAndInput
              type="phone"
              register={methods.register}
              fieldName="mobileNumber"
              topLabelCss="w-full"
              labelCss="-mb-[.3px]"
              label={t(msg.user.mobileNumber)}
              errors={methods.formState.errors}
              required
            />
          </div>
          <div>
            <button
              id="add-new-customer"
              onClick={() => {
                const user = {
                  id: methods.getValues("userId") || "",
                  firstName: methods.getValues("firstName"),
                  lastName: methods.getValues("lastName"),
                  email: methods.getValues("email"),
                  mobile: methods.getValues("mobileNumber"),
                };
                if (!user.firstName || !user.lastName || !user.email) {
                  toast.error("Please fill in all required fields");
                  return;
                }
                handleUser(user);
              }}
              type="button"
              className={`px-3 py-2 text-white rounded-md bg-blue-600`}
            >
              {t(msg.form.add)}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
